import {Component, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Location} from '@angular/common';
import {KeyCodes} from '../../../keyCodes';
import {QuestionBase} from '../question/question-base';
import {GeneralEntity} from '../../../generalEntity';
import {GeneralService} from '../../../services/general.service';
import {MessageService} from '../../../../shared/message.service';
import {QuestionService} from '../../../services/question.service';
import {ServiceAnswer, ServiceAnswerAny} from '../../../../shared/service-answer';
import {FormGroup} from '@angular/forms';
import {QuestionControlService} from '../../../services/question-control.service';
import {ButtonMeta} from '../../common/buttons/buttonMeta';
import {Subscription} from 'rxjs';
import {ServiceTools} from '../../../services/serviceTools';
import { ViewContext } from '../../../../shared/view-context';
import { getPrefixedViewUrl } from '../url-helper';

/**
 * General form view.
 */
@Component({
  selector: 'app-general-form-details',
  templateUrl: './generalForm.component.html',
  styleUrls: ['./generalForm.component.css'],
})
export class GeneralFormComponent<ENTITY extends GeneralEntity> implements OnInit, OnDestroy {

  private static CONTROL_BUTTON_ID_CANCEL = 'add';
  private static CONTROL_BUTTON_ID_SAVE = 'edit';
  private static CONTROL_BUTTON_ID_DELETE = 'delete';

  private navigationSubscription: Subscription;

  public form: FormGroup;

  // public showButtons = true;
  public isNewEntity;
  public questions: QuestionBase<any>[];

  entity: any | ENTITY;

  /**
   * Initializes the object.
   * @param {ActivatedRoute} route
   * @param {GeneralService<ENTITY extends GeneralEntity>} generalService
   * @param {Location} location
   * @param {MessageService} messagesService
   * @param {Router} router
   * @param {QuestionService} service
   * @param {QuestionControlService} questionControlService
   * @param {ViewContext} viewContext
   */
  constructor(
    protected route: ActivatedRoute,
    private generalService: GeneralService<ENTITY>,
    private location: Location,
    protected messagesService: MessageService,
    private router: Router,
    protected service: QuestionService,
    protected questionControlService: QuestionControlService,
    public viewContext: ViewContext,
  ) {
  }

  /**
   * Returns the id of the entity. Should be overwritten if the id attribute is not 'id'.
   * @returns {string} The id of the entity.
   */
  public getIdFromUrl(): string {
    return this.route.snapshot.paramMap.get('id');
  }

  /**
   * Is a new entity being created. If the entity id received is 'new' then the entity is considered to be new.
   * @returns {boolean} Is a new entity
   */
  public isNew(): boolean {
    return this.getIdFromUrl() === 'new';
  }

  /**
   * Fetches an entity to be modified. If the entity is considered new then an empty entity is
   * created. Otherwise the entity is fetched from the backend.
   * @returns {Promise<void>} When the entity is returned.
   */
  async fetchEntity(): Promise<void> {

    if (this.isNew()) {
      this.entity = {};
    } else {
      try {
        const serviceAnswer: ServiceAnswerAny = await this.generalService.getEntity(this.getIdFromUrl(), this.getRestParameters());

        this.entity = ServiceTools.getEntityFromFirstContext(serviceAnswer, this.messagesService, this.viewContext.entityNameSingular);

      } catch (error) {
        const message = `Unexpected error when fetching the entity ${this.getName(this.entity)}: ${JSON.stringify(error)}`;
        console.error(message);
        this.messagesService.addDanger(message);
      }
    }
  }

  /**
   * Returns an object for form group.
   * @param {ENTITY} entity The entity from which the values are taken.
   * @returns {any} The entity to be used as form group value.
   */
  getFormObject(entity: ENTITY): any {
    const copyEntity: ENTITY & {} = Object.assign({}, entity);
    const formObject: any = {};
    if (this.viewContext.generalFormMetaName) {
      this.questions
        .map((question: QuestionBase<any>) => question.key)
        .forEach((key: string) => {
          formObject[key] = copyEntity[key] ? copyEntity[key] : null;
        });
    }
    return formObject;
  }

  /**
   * This cancels the modification. The list view is opened and the entity in the form is not saved.
   */
  cancel(): void {
    this.navigateToList(this.getIdOfEntity(this.entity));
  }

  /**
   * Returns the id of the entity. By default the id attrivute of the entity is returned.
   * @param entity The entity from which the id is returned.
   * @returns string The id value.
   */
  protected getIdOfEntity(entity: ENTITY): string {
    return entity ? '' + entity.id : null;
  }

  /**
   * Moves the user to the list view.
   * @param {number} id The id to be selected in the list view.
   */
  navigateToList(id?: number | string): void {
    let route;
    if (id) {
      route = `/${this.viewContext.listUrl}/${id}`;
    } else {
      route = `/${this.viewContext.listUrl}/`;
    }

    const url = getPrefixedViewUrl(route);
    this.router.navigateByUrl(url);

  }

  /**
   * Handels the keyboard button pressed actions.
   * @param event The information of the action.
   */
  @HostListener('window:keydown', ['$event'])
  buttonPressed(event: any): void {
    if (event.keyCode === KeyCodes.ESCAPE) {
      this.cancel();
    }
  }

  /**
   * Sets values from form entity to the entity that is sent to rest.
   * @param {ENTITY} entity The entity to which the values are set.
   * @param formEntity The form entity from which the values are taken.
   */
  updateEntityValues(entity: ENTITY, formEntity: any): void {
    Object.keys(formEntity).forEach((key: string) => {
      entity[key] = formEntity[key];
    });
  }

  /**
   * Saves the entity by sending it to backend. If the saving is successful then the user is
   * moved to the list view.
   * @returns {Promise<void>} When saved.
   */
  async save(): Promise<void> {
    try {
      /**
       * If ViewContext has been given UI was created by the form component. Take the modified entity from the component.
       */
      this.updateEntityValues(this.entity, this.form.value);
    } catch (error) {
      const message = `Saving failed. Unexpected error when parsing the entity ${this.getName(this.entity)}: ${JSON.stringify(error)}`;
      console.error(message);
      this.messagesService.addDanger(message);
      return;
    }

    if (this.isNewEntity) { // Create
      try {
        const serviceAnswer: ServiceAnswerAny = await this.generalService.addEntity(this.entity, this.getRestParameters());
        if (serviceAnswer.isOk) {
          this.navigateToList(this.getIdOfEntity(serviceAnswer.data));
          this.messagesService.addSuccess(`Successfully created a new entity ${this.getName(this.entity)}`);
        } else {
          const errorMessage = ServiceTools.getErrorMessage(serviceAnswer, `Creating a new entity failed.`);
          console.error(errorMessage);
          this.messagesService.addDanger(errorMessage);
        }
      } catch (error) {
        const message = `Unexpected error when creating a new entity ${this.getName(this.entity)}: ${JSON.stringify(error)}`;
        console.error(message);
        this.messagesService.addDanger(message);
      }
    } else { // Update
      try {
        const serviceAnswer: ServiceAnswerAny = await this.generalService.modifyEntity(
          this.entity,
          this.getIdOfEntity(this.entity),
          this.getRestParameters());
        if (serviceAnswer.isOk) {
          this.navigateToList(this.getIdOfEntity(this.entity));
          this.messagesService.addSuccess(`Successfully updated the entity ${this.getName(this.entity)}`);
          return;
        } else {
          const errorMessage = ServiceTools.getErrorMessage(
            serviceAnswer,
            `Updating the entity ${this.getName(this.entity)} failed.`);
          console.error(errorMessage);
          this.messagesService.addDanger(errorMessage);
        }
      } catch (error) {
        const message = `Unexpected error when updating the entity ${this.getName(this.entity)}: ${JSON.stringify(error)}`;
        console.error(message);
        this.messagesService.addDanger(message);
      }
    }
  }

  /**
   * Returns the name of the entity. The name can consist of the name and the id or it can be just the another.
   * @param {ENTITY} entity The entity from which the name is taken.
   * @returns {string} The name.
   */
  private getName(entity: ENTITY): string {
    if (entity['name']) {
      return entity['name'] + ' (' + this.getIdOfEntity(this.entity) + ')';
    } else {
      return 'with id ' + this.getIdOfEntity(this.entity);
    }
  }

  /**
   * Returns array of button meta objects. These objects are used to create buttons.
   */
  public getButtonsMeta(): ButtonMeta[] {
    return [
      {
        id: GeneralFormComponent.CONTROL_BUTTON_ID_SAVE,
        text: 'Save',
        type: 'save',
        disableWhenNoSelection: true
      },
      {
        id: GeneralFormComponent.CONTROL_BUTTON_ID_CANCEL,
        text: 'Cancel',
        type: 'general',
        disableWhenNoSelection: false
      },
    ];
  }

  /**
   * When a control button has been pressed.
   * @param buttonId The id of the button.
   */
  controlButtonPressed(buttonId: string): void {
    if (buttonId === GeneralFormComponent.CONTROL_BUTTON_ID_SAVE) {
      this.save();
    } else if (buttonId === GeneralFormComponent.CONTROL_BUTTON_ID_CANCEL) {
      this.cancel();
    }
  }

  /**
   * Returns rest parameters. By default null value is returned. Objects extending this
   * class may overwrite this function if needed.
   */
  protected getRestParameters(): { key: string; value: string }[] {
    return null;
  }

  /**
   * Fetches the entity and builds the UI.
   */
  private async fetchDataAndBuildUi(): Promise<void> {
    try {
      this.isNewEntity = this.isNew();

      if (this.viewContext.generalFormMetaName) {
        this.questions = await this.service.getQuestions(this.viewContext.generalFormMetaName);
        this.form = this.questionControlService.toFormGroup(this.questions);
      }

      await this.fetchEntity();
      const formObject: any = this.getFormObject(this.entity);
      if (this.form) {
        this.form.setValue(formObject);
      }

    } catch (error) {
      this.messagesService.addDanger('Error when initializing the view: ' + error);
      throw error;
    }
  }

  /**
   * Initializes the view. Fetches the data from backend and questions for UI building.
   * @returns {Promise<void>} When done.
   */
  public async ngOnInit(): Promise<void> {

    await this.fetchDataAndBuildUi();
    /**
     * When the page is reload the entities are fetched. This happens when the context
     * (develop / production) has been changed.
     */
    this.navigationSubscription = this.router.events.subscribe(async (event: any) => {
      if (event instanceof NavigationEnd) {
        await this.contextChanged();
      }
    });
  }

  /**
   * When the context has changed.
   */
  protected async contextChanged(): Promise<void> {
    this.fetchDataAndBuildUi();
  }

  /**
   * When the view is destroyed. Navigation subscription is unsubscribed.
   */
  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }
}
